import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabaseClient";
import { apiFetch } from "@/lib/api/client";
import type { FeatureKey } from "@/components/auth/AuthProvider";
import type { AnnouncementAudience, AnnouncementIconKey } from "@/lib/landing/announcement";

export type ServiceKey = FeatureKey | "paintboothstudio";

export type PaintBoothTutorialRow = {
  id: string;
  title: string;
  youtube_url: string;
  sort_order: number;
  updated_at: string | null;
};

export type SiteSettingsRow = {
  id: boolean;
  site_name: string | null;
  site_logo_url: string | null;
  hero_heading: string | null;
  hero_subheading: string | null;
  hero_background_url: string | null;
  hero_discord_url: string | null;
  hero_services_button_label: string | null;
  hero_media_primary_title: string | null;
  hero_media_primary_description: string | null;
  hero_media_primary_url: string | null;
  hero_media_primary_type: "image" | "video" | null;
  hero_media_secondary_title: string | null;
  hero_media_secondary_description: string | null;
  hero_media_secondary_url: string | null;
  hero_media_secondary_type: "image" | "video" | null;
  // Landing-page announcement bar. Saved independently of the hero fields (see
  // useUpsertAnnouncement) so the two admin sections can't clobber each other.
  announcement_enabled: boolean | null;
  announcement_text: string | null;
  announcement_href: string | null;
  announcement_audience: AnnouncementAudience | null;
  announcement_icon: AnnouncementIconKey | null;
  announcement_cta_label: string | null;
  updated_at: string | null;
};

export type ServiceSettingsRow = {
  key: ServiceKey;
  name: string;
  tagline: string | null;
  description: string | null;
  logo_url: string | null;
  card_image_url: string | null;
  // Optional per-palette override shown to users on the "cobalt" (blue) scheme.
  // Falls back to card_image_url when null/absent. Optional so the hardcoded
  // fallback catalogs (LandingToolsGrid, AdminSiteSettingsTab) need not list it.
  card_image_url_cobalt?: string | null;
  gradient: string | null;
  href: string | null;
  sort_order: number;
  updated_at: string | null;
};

export function useSiteSettings() {
  return useQuery({
    queryKey: ["public", "site_settings"],
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 60_000,
    queryFn: async (): Promise<SiteSettingsRow | null> => {
      const { data, error } = await supabase
        .from("site_settings")
        .select("*")
        .eq("id", true)
        .maybeSingle();
      if (error) throw error;
      return (data as SiteSettingsRow | null) ?? null;
    },
  });
}

export function useServiceSettings() {
  return useQuery({
    queryKey: ["public", "service_settings"],
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 60_000,
    queryFn: async (): Promise<ServiceSettingsRow[]> => {
      const { data, error } = await supabase
        .from("service_settings")
        .select("*")
        .order("sort_order", { ascending: true });
      if (error) throw error;
      return (data as ServiceSettingsRow[] | null) ?? [];
    },
  });
}

export function usePaintBoothTutorials() {
  return useQuery({
    queryKey: ["public", "paintbooth_tutorials"],
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 60_000,
    queryFn: async (): Promise<PaintBoothTutorialRow[]> => {
      return apiFetch<PaintBoothTutorialRow[]>("/api/public/tutorial-videos");
    },
  });
}

export function useUpsertSiteSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      input: Pick<
        SiteSettingsRow,
        | "site_name"
        | "site_logo_url"
        | "hero_heading"
        | "hero_subheading"
        | "hero_background_url"
        | "hero_discord_url"
        | "hero_services_button_label"
        | "hero_media_primary_title"
        | "hero_media_primary_description"
        | "hero_media_primary_url"
        | "hero_media_primary_type"
        | "hero_media_secondary_title"
        | "hero_media_secondary_description"
        | "hero_media_secondary_url"
        | "hero_media_secondary_type"
      >
    ) => {
      const { error } = await supabase.from("site_settings").upsert({
        id: true,
        site_name: input.site_name,
        site_logo_url: input.site_logo_url,
        hero_heading: input.hero_heading,
        hero_subheading: input.hero_subheading,
        hero_background_url: input.hero_background_url,
        hero_discord_url: input.hero_discord_url,
        hero_services_button_label: input.hero_services_button_label,
        hero_media_primary_title: input.hero_media_primary_title,
        hero_media_primary_description: input.hero_media_primary_description,
        hero_media_primary_url: input.hero_media_primary_url,
        hero_media_primary_type: input.hero_media_primary_type,
        hero_media_secondary_title: input.hero_media_secondary_title,
        hero_media_secondary_description: input.hero_media_secondary_description,
        hero_media_secondary_url: input.hero_media_secondary_url,
        hero_media_secondary_type: input.hero_media_secondary_type,
        updated_at: new Date().toISOString(),
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["public", "site_settings"] });
      queryClient.invalidateQueries({ queryKey: ["public", "landing"] });
    },
  });
}

/**
 * Saves only the announcement-bar columns of the singleton site_settings row.
 *
 * Separate from useUpsertSiteSettings on purpose: an upsert writes just the
 * columns present in its payload, so a narrow mutation lets the Announcement
 * section save without flushing half-typed hero edits sitting unsaved in the
 * Brand And Hero section (and vice versa).
 *
 * Authorization is server-side — the browser `supabase` facade posts to
 * /api/db/query, which requires an admin session for any non-select.
 */
export function useUpsertAnnouncement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      input: Pick<
        SiteSettingsRow,
        | "announcement_enabled"
        | "announcement_text"
        | "announcement_href"
        | "announcement_audience"
        | "announcement_icon"
        | "announcement_cta_label"
      >,
    ) => {
      const { error } = await supabase.from("site_settings").upsert({
        id: true,
        announcement_enabled: input.announcement_enabled,
        announcement_text: input.announcement_text,
        announcement_href: input.announcement_href,
        announcement_audience: input.announcement_audience,
        announcement_icon: input.announcement_icon,
        announcement_cta_label: input.announcement_cta_label,
        updated_at: new Date().toISOString(),
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["public", "site_settings"] });
      queryClient.invalidateQueries({ queryKey: ["public", "landing"] });
    },
  });
}

export function useUpsertServiceSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      input: Pick<
        ServiceSettingsRow,
        | "key"
        | "name"
        | "tagline"
        | "description"
        | "logo_url"
        | "card_image_url"
        | "card_image_url_cobalt"
        | "gradient"
        | "href"
        | "sort_order"
      >
    ) => {
      const { error } = await supabase.from("service_settings").upsert({
        key: input.key,
        name: input.name,
        tagline: input.tagline,
        description: input.description,
        logo_url: input.logo_url,
        card_image_url: input.card_image_url,
        card_image_url_cobalt: input.card_image_url_cobalt ?? null,
        gradient: input.gradient,
        href: input.href,
        sort_order: input.sort_order,
        updated_at: new Date().toISOString(),
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["public", "service_settings"] });
      queryClient.invalidateQueries({ queryKey: ["public", "landing"] });
    },
  });
}

export function useReplacePaintBoothTutorials() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: Array<Pick<PaintBoothTutorialRow, "id" | "title" | "youtube_url" | "sort_order">>) => {
      const existing = await supabase
        .from("paintbooth_tutorials")
        .select("id");

      if (existing.error) {
        throw existing.error;
      }

      const nextIds = new Set(input.map((row) => row.id).filter(Boolean));
      const existingIds = ((existing.data as Array<{ id: string }> | null) ?? []).map((row) => row.id);
      const idsToDelete = existingIds.filter((id) => !nextIds.has(id));

      if (idsToDelete.length > 0) {
        const { error: deleteError } = await supabase
          .from("paintbooth_tutorials")
          .delete()
          .in("id", idsToDelete);
        if (deleteError) throw deleteError;
      }

      if (input.length > 0) {
        const { error: upsertError } = await supabase.from("paintbooth_tutorials").upsert(
          input.map((row, index) => ({
            id: row.id,
            title: row.title,
            youtube_url: row.youtube_url,
            sort_order: row.sort_order ?? index,
            updated_at: new Date().toISOString(),
          })),
        );
        if (upsertError) throw upsertError;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["public", "paintbooth_tutorials"] });
      queryClient.invalidateQueries({ queryKey: ["public", "landing"] });
    },
  });
}

export type LandingData = {
  siteSettings: SiteSettingsRow | null;
  serviceSettings: ServiceSettingsRow[];
  tutorialVideos: PaintBoothTutorialRow[];
};

export function useLandingData() {
  return useQuery({
    queryKey: ["public", "landing"],
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 60_000,
    queryFn: async (): Promise<LandingData> => {
      return apiFetch<LandingData>("/api/public/landing");
    },
  });
}
